import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Button } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { UserStore } from '../shared/user/store/user.store';
import { ConfigService } from '../shared/services/config-service';
import { InputTextModule } from 'primeng/inputtext';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FormsModule } from '@angular/forms';
import { ApiKeyStore } from '../shared/user/store/api-key.store';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  selector: 'app-user-profile',
  imports: [
    CommonModule,
    TableModule,
    CardModule,
    Button,
    ToastModule,
    ConfirmDialogModule,
    FormsModule,
    TagModule,
    InputTextModule,
    ClipboardModule,
    DialogModule,
    CheckboxModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.css',
})
export class UserProfileComponent implements OnInit {
  newKeyLabel: string = '';
  showCreateDialog: boolean = false;
  showOldRevoked: boolean = false;
  newKeyLabelInvalid: boolean = false;

  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly configService = inject(ConfigService);
  readonly userStore = inject(UserStore);
  private readonly confirmationService = inject(ConfirmationService);

  protected readonly apiKeyStore = inject(ApiKeyStore);
  protected userData$ = this.oidcSecurityService.userData$;
  protected userProfile = this.userStore.userProfile;
  protected isAuthenticated$ = this.oidcSecurityService.isAuthenticated$;

  ngOnInit(): void {
    if(this.userStore.isRegisteredNectaOne()) {
      this.apiKeyStore.loadApiKeys();
    }
  }

  openAuthUserProfile() {
    const url =
      this.configService.getUrlNectaAuthentificationServer() +
      '/Identity/Account/Manage/Index';
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  getUserRole(roleId: number): string {
    switch (roleId) {
      case 0: return 'User';
      case 1: return 'Controller';
      case 2: return 'Sysadmin';
      case 3: return 'MBS';
      default: return 'Unknown';
    }
  }

  createApiKey(): void {
    if (!this.newKeyLabel?.trim()) {
      this.newKeyLabelInvalid = true;
      return;
    }
    this.newKeyLabelInvalid = false;
    this.apiKeyStore.createApiKey(this.newKeyLabel.trim());
    this.newKeyLabel = '';
    this.showCreateDialog = false;
  }

   confirmRotate(keyHash: string): void {
    this.confirmationService.confirm({
      message: 'Möchten Sie diesen API-Key wirklich erneuern? Der alte Schlüssel wird ungültig.',
      header: 'API-Key erneuern',
      icon: 'pi pi-refresh',
      acceptLabel: 'Erneuern',
      rejectLabel: 'Abbrechen',
      acceptButtonStyleClass: 'p-button-warning',
      accept: () => {
        this.apiKeyStore.rotateApiKey(keyHash);
      },
    });
  }

  confirmRevoke(keyHash: string): void {
    this.confirmationService.confirm({
      message: 'Möchten Sie diesen API-Key wirklich widerrufen? Diese Aktion kann nicht rückgängig gemacht werden.',
      header: 'API-Key widerrufen',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Widerrufen',
      rejectLabel: 'Abbrechen',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.apiKeyStore.revokeApiKey(keyHash);
      },
    });
  }

  dismissNewKey(): void {
    this.apiKeyStore.clearNewKey();
  }

  get filteredApiKeys() {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return this.apiKeyStore.apiKeys().filter((key) => {
      if (!key.revokedAt) return true;
      if (this.showOldRevoked) return true;
      return new Date(key.revokedAt) >= oneMonthAgo;
    });
  }

  copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value);
  }
}